import './EmailFormatTag.scss';

interface EmailFormatTagProps {
  /** Pattern tokens, e.g. "{first}.{last}". */
  format: string;
  /** Rendered after the pattern as "@domain". Omit where the domain is already established. */
  domain?: string;
  /** Share of the domain's contacts on this pattern, rendered inside the tag. */
  percentage?: number;
  /** Contacts on this pattern. With domainTotal, fills out the tooltip's counts. */
  contactCount?: number;
  /** Contacts on the domain that matched any pattern. Pass with percentage for a tooltip. */
  domainTotal?: number;
  size?: 'small' | 'normal';
  bg?: 'hard' | 'medium';
  onClick?: () => void;
}

/**
 * An email pattern as a tag, shaped like RoleTag. The pattern and the domain are colored
 * differently so the part that varies per person reads apart from the part that does not.
 */
export function EmailFormatTag({
  format,
  domain,
  percentage,
  contactCount,
  domainTotal,
  size = 'normal',
  bg = 'hard',
  onClick,
}: EmailFormatTagProps) {
  if (!format) return null;

  const sizeClass = size === 'small' ? ' email-format-tag--small' : '';
  const bgClass = bg === 'medium' ? ' email-format-tag--bg-medium' : '';
  const clickableClass = onClick ? ' email-format-tag--clickable' : '';

  const tooltip = buildTooltip({ domain, percentage, contactCount, domainTotal });

  return (
    <span
      className={`email-format-tag${sizeClass}${bgClass}${clickableClass}`}
      onClick={onClick}
      data-tooltip-id={tooltip ? 'main-tooltip' : undefined}
      data-tooltip-content={tooltip}
    >
      <span className="email-format-tag__main">
        <span className="email-format-tag__pattern">{format}</span>
        {domain && <span className="email-format-tag__domain">@{domain}</span>}
      </span>
      {percentage !== undefined && (
        <span className="email-format-tag__percentage">{Math.round(percentage)}%</span>
      )}
    </span>
  );
}

/**
 * Says what the percentage actually measures: the share of contacts already found on the
 * domain, not a prediction about addresses we have not seen.
 */
function buildTooltip({
  domain,
  percentage,
  contactCount,
  domainTotal,
}: Pick<EmailFormatTagProps, 'domain' | 'percentage' | 'contactCount' | 'domainTotal'>): string | undefined {
  if (percentage === undefined) return undefined;

  const where = domain ? `on ${domain}` : 'at this institution';
  const counts =
    contactCount !== undefined && domainTotal !== undefined
      ? ` (${contactCount.toLocaleString()} of ${domainTotal.toLocaleString()})`
      : '';

  return `${Math.round(percentage)}% of contacts found ${where} follow this format${counts}`;
}
